import {
  hasOfferMarker,
  OFFER_MARKER_REGEX,
  type OfferKind,
} from '@revelio/core/global-ai-assistant/offer-marker';

export { hasOfferMarker, OFFER_MARKER_REGEX, type OfferKind };

export type OfferSegment =
  | { type: 'text'; text: string }
  | { type: 'offer'; offer: OfferKind; text: string };

/**
 * Replace offer markers with their visible phrase (plain text). With `kind`,
 * only markers of that kind are downgraded; other kinds stay intact. Used when
 * a marker's backing action doesn't exist (e.g. an unresolved page id) so the
 * narration never shows a dead trigger.
 */
export function stripOfferMarkers(text: string, kind?: OfferKind): string {
  const re = new RegExp(OFFER_MARKER_REGEX.source, 'g');
  return text.replace(re, (full: string, phrase: string, markerKind: string) =>
    kind === undefined || markerKind === kind ? phrase : full
  );
}

/**
 * Split assistant narration into ordered segments so the FE can render offer
 * markers as styled trigger spans and everything else as plain text. Never
 * throws; unrecognized bracket/paren text passes through verbatim.
 */
export function parseOfferSegments(text: string): OfferSegment[] {
  const re = new RegExp(OFFER_MARKER_REGEX.source, 'g');
  const segments: OfferSegment[] = [];
  let lastIndex = 0;
  for (const match of text.matchAll(re)) {
    const start = match.index ?? 0;
    if (start > lastIndex) {
      segments.push({ type: 'text', text: text.slice(lastIndex, start) });
    }
    segments.push({
      type: 'offer',
      offer: match[2] as OfferKind,
      // match[1]/[2] are always present when the regex matches; the `?? ''`
      // satisfies strict indexed-access typing (noUncheckedIndexedAccess).
      text: match[1] ?? '',
    });
    lastIndex = start + match[0].length;
  }
  if (lastIndex < text.length) {
    segments.push({ type: 'text', text: text.slice(lastIndex) });
  }
  // Collapse to a single text segment when nothing matched (keeps the no-marker
  // path allocation-free and the equality simple for callers/tests).
  return segments.length === 0 ? [{ type: 'text', text }] : segments;
}
